/**
 * PAYMENTS, at `/payments`, a client's alone. Built from
 * `faithfully-kept-business/design/home/Payments.dc.html`, its first frame's structure and words,
 * with ops's copy of the household's invoices in place of the sample ones, as GET /api/payments
 * answers them. The words are in `words.ts`, which says why each of the artboard's other lines is
 * not drawn.
 *
 * - THE ARTBOARD'S PLACE: "Payments" over "To pay"; a card for each invoice owed, in the Worker's
 *   order, newest first, with its clean's day, what is still to pay, its lines and "Pay <amount>",
 *   which leads to the invoice's own page, where the ways to pay are; then "Paid", each invoice paid
 *   as its clean's day and its total. Every amount is written as the artboards write one, "$212",
 *   and "$190.50" only when there are cents.
 * - EACH PAID ROW IS THE WAY TO ITS INVOICE, which no artboard draws: the whole row one link to the
 *   invoice's own page, with the chevron Home's next visit leads on with, so a paid invoice can
 *   still be read, and its page says Paid as ops does. Raised by the owner's walk of 2026-10-16,
 *   replayed: once an invoice was paid, nothing on home opened it.
 * - ON ITS WAY, between To pay and Paid, which no artboard draws (provisional P35): an invoice she
 *   has just paid on Stripe's page, in any tab of her browser, and one ops says a bank payment on is
 *   clearing (`pay.bank_payment_clearing`), each as a row like Paid's, its day, its home and ops's
 *   balance in body type, with her payment on its way or the seam's sentence for a bank payment
 *   clearing, and never Pay or a due day, since there is nothing to pay from home on it now. Each
 *   row leads to the invoice's own page. It never says paid: an invoice goes under Paid only once
 *   ops pushes it paid (binding rule 1). With only such invoices owed, To pay says the artboard's
 *   "Nothing right now.", as its "Back in home" frame does, and as Home and the nav say. Raised by
 *   the owner's walk of 2026-10-16, replayed: back from paying, Payments drew the amount under To
 *   pay in its largest type while Home and the nav said nothing was owed.
 * - WITH SEVERAL HOMES, which no artboard draws, each card and each row names its clean's home
 *   under its day, by the label ops typed alone, in body type and never muted, so two invoices of
 *   one day at two homes read apart at a glance (`homeNameOf`, provisional P45, as P40); a card and
 *   its Pay are known by both. Two still known alike, the same day at the same home or at none
 *   named, each say their first line as ops wrote it under that, where those differ (`tellApart`):
 *   ops's words, never a number of home's. With one home nothing is named, so the artboard stays as
 *   drawn.
 * - EVERY CARD SAYS WHEN IT IS DUE under its amount, in the words and the size its own page uses
 *   (`owedStateOf`): sent, the day it is due; part paid, what is still to pay of ops's total, then
 *   that day, so a balance below the lines that add up to the total is never left unexplained;
 *   overdue, the day it was due. The artboard draws no due day; ops's own invoice page gives one,
 *   and the owner's walk of 2026-10-16 found home's page without it. A day that is not real says
 *   nothing, so a card ops gives none is the artboard's.
 * - OPS DECIDES, AND HOME MARKS NOTHING PAID (CLAUDE.md binding rule 1, the seam l.362-363 at
 *   b727ec1). An invoice is owed or Paid exactly as the Worker's lists say, which are ops's states;
 *   one owed is under To pay, or under On its way while she has just paid it or ops says a bank
 *   payment on it clears. Pay is offered only where ops says there is something to pay from home
 *   now, no saved method pays it, and ops offers a way to pay it from home, Zelle or the bank and
 *   card, so Pay never leads to a page that says only to call. Otherwise the card says why, when the
 *   saved method pays it, and still leads to the invoice's own page, where she says how the clean
 *   went, which a saved method waits on.
 * - AN INVOICE THE SAVED METHOD PAYS says so on its card, in one line from the words of its own page
 *   (`savedMethodLine`), so she does not pay it again: at the stage ops last pushed, and after the
 *   face ops says counts, but for Good, which may name an area, which holds the charge, and so says
 *   nothing of when, as its page thanks her plainly (round four, slice F1, ops's section 4 at
 *   9f10a8b; the saved method above the cards still says it charges automatically, and the card
 *   offers no Pay). None is said once ops says the household's method is stopped or nothing is
 *   saved, which the card above says, but that a charge ops says is at Stripe is being charged now:
 *   a Stop does not cancel it (the seam, l.540 at b727ec1).
 * - PAID: a paid invoice says how and when each payment on it came, as ops recorded it (its section
 *   11.2, `payments`, round five's slice B3, P62), in the artboard's own form, "From your bank
 *   account, 2 October. Receipt emailed." and "By card, today. Receipt emailed.", each led by what
 *   that payment came to (`paymentWithAmount`, round six, slice S1): "$212 from your bank account, 2
 *   October. Receipt emailed.", so a part payment made before never reads as the whole beside the
 *   row's own amount; each on its own line, a refund as what went back and a correction as the
 *   office's adjustment, and that a receipt was emailed only where ops says so. Where ops sends no
 *   payment it says how the saved method charged it, when ops says so, and never both, so one
 *   payment is never said twice. A row under On its way says, under what is on its way, what is
 *   still to pay of a part-paid invoice (`partPaidOf`) and then the payments already made on it,
 *   each with its amount; a card under To pay says none, since what is still to pay is what it
 *   says.
 * - THE SAVED METHOD, in the seam's words, under the heading and above the cards, so a card with no
 *   Pay is explained above it. Its day has no year, as the seam writes it.
 * - STOP, the seam's button under it, whenever ops says a method stands (the seam, l.197-199 at
 *   b727ec1): ops holds whether Stripe is on per invoice and not per household, so Stop is drawn
 *   all the same, and a Stop that finds Stripe off at ops says so. It opens the Stop sheet
 *   (`components/StopSheet.tsx`), which asks for a fresh code when the door does. Ops decides here
 *   too (binding rule 1): a Stop sent says "Sent to the office." and offers no Stop while it is on
 *   its way, and the method is said to be stopped, in place of it, only once ops's receipt says so
 *   (`stopAsk`, as the Worker reads it), or its push says nothing is saved after the Stop sent
 *   from this page. While the Stop is on its way, Payments is read again every three seconds, at
 *   most six times, so what ops answers is said without a reload. The card and this one Stop state
 *   are Account's How you pay's too (`components/SavedMethod.tsx`, slice A2).
 * - NOTHING SAVED IS NEVER SILENCE (slice A2): where no method stands, the card says "You pay each
 *   invoice yourself." with the way to How you pay, where she reads how automatic payments start;
 *   once ops says the method stopped or nothing is saved, that card leads there too. Not on the page
 *   Stripe sends her back to, which says what she has just done, nor while the invoices are still
 *   coming down, nor while ops says a saved method is charging an owed invoice now, which a Stop
 *   does not cancel and that invoice's card says (`paysYourself`).
 * - NOTHING YET: while the invoices are still coming down, the heading and the line that the rest
 *   is on its way; with nothing owed but what is on its way, the artboard's "Nothing right now."
 *   under To pay; no On its way with nothing on its way, and no Paid with nothing paid.
 * - A MEMBER IS SHOWN NO MONEY. The nav offers her no Payments tab, and the Worker answers her 404
 *   here, as it does a person with no household, so a 404 goes to Home, whose own read says which
 *   she is: Home for a member, the page that says to call for nobody.
 * - BACK FROM PAYING ON STRIPE'S PAGE in any tab (`lib/paying.ts`), the invoice she paid is under On
 *   its way, until ops's push says how it stands. The amount stays, since it is ops's balance until
 *   ops says otherwise. A page already open in another tab moves it there at once
 *   (`components/Guarded.tsx`). The page Stripe sends her back to (`PaymentReturn.tsx`) is this
 *   place, with what it says drawn above To pay (`above`), as the artboard's third frame draws
 *   "Thank you", told whether a card below offers Pay, so it never points at a Pay not drawn.
 * - ITS NUMBER (round four, slice M2, ops's section 11.2): "Invoice FK-42" under each card's and
 *   each row's day and home, To pay, On its way and Paid alike, in the body colour, so two invoices
 *   of one day differ at a glance; nothing for one sent before numbering, so the artboard stays as
 *   drawn.
 * - PAY LANDS ON THE WAYS TO PAY (slice M2): Pay hands the invoice's page `pay` in the history, and
 *   the page brings its ways to pay into view, clear of the nav (`screens/Invoice.tsx`). On the page
 *   Stripe's back arrow returns to, the Pay on the invoice she left opens its page on the way she
 *   left it from, the bank or the card, as "Back to your invoice" did (`leftFor`, P35).
 * - SENT BY ZELLE, AS SOMEBODY ON THE HOUSEHOLD SAID on its page (round four, slice M1, P53): the
 *   Worker keeps the word and carries it while it holds, so on every device of every client of the
 *   household the invoice is under On its way, its row naming the day it was said, and never who
 *   else said it; a page open in another tab of hers reads itself again when hers changes.
 */
import { useEffect, useId, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { getPayments, type Invoice, type PaymentsPlace } from '../api.ts';
import { CARD_BUTTON, NAVY_BUTTON, TEXT_LINK } from '../components/buttons.ts';
import { Onward } from '../components/Chevron.tsx';
import { Guarded, type Signed } from '../components/Guarded.tsx';
import { SavedMethodCard, useStop } from '../components/SavedMethod.tsx';
import { chargeClaimed, savedMethodLine } from '../components/SavedMethodPays.tsx';
import { Shell, useOnItsWay } from '../components/Shell.tsx';
import { dateInWords } from '../lib/dates.ts';
import {
  daysOf,
  invoiceHeading,
  onItsWayLine,
  owedStateOf,
  partPaidOf,
  payableHere,
  tellApart,
  wayFromHome,
} from '../lib/invoices.ts';
import { forget, type Left, type LeftFrom, type OpenOn } from '../lib/paying.ts';
import { homeNameOf } from '../lib/visits.ts';
import { ACCOUNT_PAYING_PATH, HOME_PATH, invoicePath } from '../paths.ts';
import {
  ACCOUNT,
  chargedTo,
  dollars,
  invoiceNumbered,
  invoiceOf,
  PAYING,
  PAYMENTS,
  payAmount,
  paymentWithAmount,
  REST_ON_ITS_WAY,
} from '../words.ts';

const EYEBROW = 'font-mono text-small tracking-widest text-text-emphasis uppercase';

export function Payments(signed: Signed) {
  return <PaymentsShown signed={signed} />;
}

interface ShownProps {
  readonly signed: Signed;
  /**
   * Drawn above To pay, in place of the place's own heading, which To pay then heads a section
   * under, as the artboard's third frame draws it; and drawn alone in the shell when Payments cannot
   * be read, since it says what needs no read, unless `aboveAlone` is given for that. Told whether a
   * card below offers Pay (`payBelow`), so what it says never points at a Pay that is not on the
   * page: none where nothing is owed, where every invoice owed is on its way, where no card owed
   * offers Pay, while the invoices are still coming down, and where Payments cannot be read.
   */
  readonly above?: (payBelow: boolean) => ReactNode;
  /** Drawn alone in the shell in place of `above` when Payments cannot be read. */
  readonly aboveAlone?: ReactNode;
  /**
   * The invoice this tab left to pay on Stripe's page and came back from unpaid, with the way she
   * left it from: the Pay on its card opens its page on that way, as the way back did (P35).
   */
  readonly leftFor?: Left | null;
}

/** Payments, read and drawn, with what `above` says above To pay where it is given. */
export function PaymentsShown({ signed, above, aboveAlone, leftFor = null }: ShownProps) {
  // Nothing is drawn below what is said alone, so no Pay is below it.
  const alone = aboveAlone ?? above?.(false);
  return (
    <Guarded
      load={getPayments}
      signed={signed}
      at="payments"
      heading={PAYMENTS.eyebrow}
      notFound={<Navigate to={HOME_PATH} replace />}
      failed={alone === undefined ? undefined : <Shell at="payments">{alone}</Shell>}
    >
      {(place, reload) => (
        <PaymentsDrawn
          place={place}
          above={above}
          leftFor={leftFor}
          signed={signed}
          reload={reload}
        />
      )}
    </Guarded>
  );
}

interface DrawnProps {
  readonly place: PaymentsPlace;
  readonly above?: (payBelow: boolean) => ReactNode;
  readonly leftFor: Left | null;
  readonly signed: Signed;
  readonly reload: () => void;
}

/**
 * Whether an invoice owed is on its way, and so drawn under On its way and not To pay: somebody on
 * the household said she sent it by Zelle and the Worker says the word holds (P53), or she came back
 * from paying it on Stripe's page in this tab or another of her browser's, as the place draws it
 * (`held`), and ops has not yet said otherwise (`onItsWayLine`); or ops says a bank payment on it is
 * clearing.
 */
const comingIn = (invoice: Invoice, held: (invoiceId: string) => boolean): boolean =>
  invoice.pay.clearing || onItsWayLine(invoice, held(invoice.id)) !== null;

/**
 * What the Pay on an owed invoice's card offers, in cents, or null where its card draws no Pay: only
 * where there is something to pay on it from here now (`payableHere`) and ops offers a way to pay it
 * from home, since without one its page says only to call (`wayFromHome`).
 */
const payOffered = (invoice: Invoice): number | null =>
  wayFromHome(invoice.pay) ? payableHere(invoice) : null;

function PaymentsDrawn({ place, above, leftFor, signed, reload }: DrawnProps) {
  const { toPay, paid, homes } = place;
  const arriving = place.arriving.invoices;
  // As the place draws the payments on their way, so the nav counts what this page offers.
  const held = useOnItsWay();
  const owing = toPay.filter((invoice) => !comingIn(invoice, held));
  const coming = toPay.filter((invoice) => comingIn(invoice, held));
  const nothingOwed = owing.length === 0;
  // Whether a card under To pay offers Pay, which what is drawn above To pay is told, so it never
  // points at a Pay that is not on the page (the review of 574a196).
  const payBelow = !arriving && owing.some((invoice) => payOffered(invoice) !== null);

  // The saved method and her Stop, one state with How you pay's (`components/SavedMethod.tsx`).
  const stop = useStop(place, signed, reload);

  // Ops's push has said how each invoice with nothing to pay on it here stands, so none of them is
  // on its way in any tab any more, and a push that offers one again offers its Pay.
  useEffect(() => {
    for (const invoice of [...toPay, ...paid]) {
      if (payableHere(invoice) === null) forget(invoice.id);
    }
  }, [toPay, paid]);

  return (
    <Shell at="payments" payments over={stop.sheet}>
      {above?.(payBelow)}
      <div>
        {above === undefined ? (
          <>
            <p className={EYEBROW}>{PAYMENTS.eyebrow}</p>
            <h1 className="mt-1 font-serif text-h2 font-semibold text-pretty text-text-body">
              {PAYMENTS.toPay}
            </h1>
          </>
        ) : (
          <h2 className="font-serif text-h3 font-semibold text-text-body">{PAYMENTS.toPay}</h2>
        )}
        {arriving ? (
          <p className="mt-2 text-body text-pretty">{REST_ON_ITS_WAY}</p>
        ) : nothingOwed ? (
          <p className="mt-1 text-small text-text-muted">{PAYMENTS.nothingNow}</p>
        ) : null}
      </div>

      {stop.card !== null ? (
        <SavedMethodCard {...stop.card}>
          {stop.methodStands ? null : <HowYouPayLink />}
        </SavedMethodCard>
      ) : stop.paysYourself && !arriving && above === undefined ? (
        <PaysYourself />
      ) : null}

      {nothingOwed ? null : (
        <OwedList toPay={owing} homes={homes} methodStands={stop.methodStands} leftFor={leftFor} />
      )}

      {coming.length > 0 ? (
        <OnItsWayList coming={coming} homes={homes} held={held} today={place.today} />
      ) : null}

      {paid.length > 0 ? <PaidList paid={paid} homes={homes} today={place.today} /> : null}
    </Shell>
  );
}

/**
 * Where nothing is saved: that she pays each invoice herself, in the card the saved method is said
 * in, and the way to How you pay, where she reads how automatic payments start (slice A2). Never on
 * the page Stripe sends her back to, which says what she has just done, and after her tick there
 * that she asked us to charge her bank account automatically, which ops saves only once the payment
 * clears; nor while the invoices are still coming down, when nothing yet says whether one is saved;
 * nor while ops says a saved method is charging an owed invoice now, which its card says
 * (`paysYourself`).
 */
function PaysYourself() {
  return (
    <div
      data-pays-yourself
      className="rounded-card border border-border-subtle bg-surface-raised px-5 py-4"
    >
      <p className="text-body text-pretty text-text-body">{ACCOUNT.youPayEachInvoice}</p>
      <HowYouPayLink />
    </div>
  );
}

/** The way to How you pay, under what the card says, with the chevron that says it leads on. */
function HowYouPayLink() {
  return (
    <Link to={ACCOUNT_PAYING_PATH} className={`gap-1 text-body ${TEXT_LINK}`}>
      {ACCOUNT.howYouPay}
      <Onward />
    </Link>
  );
}

interface OwedListProps {
  readonly toPay: readonly Invoice[];
  readonly homes: PaymentsPlace['homes'];
  /** The household's saved method stands, as ops has it, so a card may say what it will do. */
  readonly methodStands: boolean;
  /** The invoice this tab came back from unpaid, and the way she left it from. */
  readonly leftFor: Left | null;
}

/** To pay's cards, each named by its clean's home among several, and told apart where alike. */
function OwedList({ toPay, homes, methodStands, leftFor }: OwedListProps) {
  const apart = tellApart(toPay, homes);
  return (
    <ul className="flex flex-col gap-5">
      {toPay.map((invoice) => (
        <li key={invoice.id}>
          <OwedCard
            invoice={invoice}
            home={homeNameOf(invoice, homes)}
            apart={apart.get(invoice.id)}
            methodStands={methodStands}
            way={leftFor?.invoiceId === invoice.id ? leftFor.way : undefined}
          />
        </li>
      ))}
    </ul>
  );
}

interface OwedCardProps {
  readonly invoice: Invoice;
  /** The name of its clean's home, when she sees several homes (`homeNameOf`). */
  readonly home: string | undefined;
  /** Its first line's words, where another card is named as this one is (`tellApart`). */
  readonly apart: string | undefined;
  /** The household's saved method stands, as ops has it, so a card may say what it will do. */
  readonly methodStands: boolean;
  /** The way she left this invoice from for Stripe's page, which its Pay opens its page on. */
  readonly way: LeftFrom | undefined;
}

/** The ids of the elements drawn, as one reference, or none with none drawn. */
function idsOf(...ids: readonly (string | false)[]): string | undefined {
  const drawn = ids.filter((id): id is string => id !== false);
  return drawn.length === 0 ? undefined : drawn.join(' ');
}

/**
 * An invoice owed, as the artboard's card: its day, its home's name, its number and what tells it
 * apart where each is given, what is still to pay, its lines, and Pay; or what stands in place of
 * Pay, and the way to its own page. The card and its link are known by the day, the home, the
 * number and what tells it apart together. Never one on its way (`comingIn`), which On its way
 * draws.
 *
 * PAY LANDS ON THE WAYS TO PAY (slice M2 of round four, after the walk was replayed, where she
 * landed at the top of the page with nothing on screen to pay with): its link hands the invoice's
 * page `pay` in the history, never in the address, and the page brings its ways to pay into view;
 * and, on the invoice she came back from unpaid, the way she left it from, which the page opens on
 * (`OpenOn`).
 */
function OwedCard({ invoice, home, apart, methodStands, way }: OwedCardProps) {
  const headingId = useId();
  const homeId = useId();
  const numberId = useId();
  const apartId = useId();
  const heading = invoiceHeading(invoice);
  const lines = invoice.lines.flatMap((line) =>
    line.description === undefined ? [] : [{ ...line, description: line.description }],
  );
  // The day it is due, part paid or overdue, as its own page says it, so a balance below its lines
  // is explained.
  const state = owedStateOf(invoice);
  // Pay, only where ops offers a way to pay it from home: without one its page says only to call.
  const offered = payOffered(invoice);
  // A Stop leaves a charge at Stripe as it stands, so that one is still said once the method is not.
  const charge = methodStands || chargeClaimed(invoice) ? savedMethodLine(invoice) : null;
  const { number } = invoice;
  const describedBy = idsOf(
    heading !== null && headingId,
    home !== undefined && homeId,
    number !== undefined && numberId,
    apart !== undefined && apartId,
  );

  return (
    <article
      aria-labelledby={describedBy}
      className="rounded-card border-2 border-border-brand bg-surface-brand-soft p-6"
    >
      {heading === null ? null : (
        <h2 id={headingId} className="text-small text-text-muted">
          {heading}
        </h2>
      )}
      {home === undefined ? null : (
        <p id={homeId} className="text-body text-text-body">
          {home}
        </p>
      )}
      {number === undefined ? null : (
        <p id={numberId} className={NUMBER}>
          {invoiceNumbered(number)}
        </p>
      )}
      {apart === undefined ? null : (
        <p id={apartId} className="text-small text-pretty wrap-break-word text-text-muted">
          {apart}
        </p>
      )}
      {invoice.balanceCents === undefined ? null : (
        <p className="mt-1 font-serif text-price font-semibold text-text-brand">
          {dollars(invoice.balanceCents)}
        </p>
      )}
      {state === null ? null : <p className="mt-2 text-body text-pretty text-text-body">{state}</p>}
      {lines.length > 0 ? (
        <ul className="mt-4 flex flex-col gap-1 border-t border-border-subtle pt-3">
          {lines.map((line, index) => (
            <li key={index} className="flex justify-between gap-3">
              <span className="min-w-0 text-small wrap-break-word text-text-body">
                {line.description}
              </span>
              {line.amountCents === undefined ? null : (
                <span className="shrink-0 text-small font-semibold text-text-body">
                  {dollars(line.amountCents)}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {charge === null ? null : (
        <p className="mt-4 text-small text-pretty text-text-body">{charge}</p>
      )}
      {offered === null ? (
        <Link
          to={invoicePath(invoice.id)}
          aria-describedby={describedBy}
          className={`mt-5 ${CARD_BUTTON}`}
        >
          {PAYMENTS.see}
        </Link>
      ) : (
        <Link
          to={invoicePath(invoice.id)}
          state={(way === undefined ? { pay: true } : { pay: true, way }) satisfies OpenOn}
          aria-describedby={describedBy}
          className={`mt-5 ${NAVY_BUTTON}`}
        >
          {payAmount(offered)}
        </Link>
      )}
    </article>
  );
}

/**
 * An invoice's number as a card and a row draw it, under its day and its home: small, in the body
 * colour and never muted, so two of one day differ at a glance.
 */
const NUMBER = 'text-small text-text-body';

interface RowsProps {
  readonly heading: string;
  readonly invoices: readonly Invoice[];
  readonly homes: PaymentsPlace['homes'];
  /** What a row says under its day and its home, each line in turn, or none. */
  readonly said: (invoice: Invoice) => readonly string[];
  /** Whether those lines are drawn quiet, as the artboard draws how a paid invoice was paid. */
  readonly quiet: boolean;
  /** The amount a row ends with, as ops gives it, or undefined for none. */
  readonly amount: (invoice: Invoice) => number | undefined;
}

/**
 * A section of rows under its heading, as the artboard draws Paid: each invoice as its day, its
 * home's name and what tells it apart where either is given, a line about it, and an amount, the
 * whole row the way to the invoice's own page.
 */
function Rows({ heading, invoices, homes, said, quiet, amount }: RowsProps) {
  const headingId = useId();
  const apart = tellApart(invoices, homes);
  return (
    <section aria-labelledby={headingId}>
      <h2 id={headingId} className="font-serif text-h3 font-semibold text-text-body">
        {heading}
      </h2>
      <ul className="mt-3 rounded-card border border-border-subtle bg-surface-raised px-5">
        {invoices.map((invoice) => (
          <li key={invoice.id} className="border-b border-border-subtle py-1 last:border-b-0">
            <InvoiceRow
              invoice={invoice}
              home={homeNameOf(invoice, homes)}
              apart={apart.get(invoice.id)}
              said={said(invoice)}
              quiet={quiet}
              amount={amount(invoice)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * "On its way": each invoice owed that somebody on the household said she sent by Zelle, naming the
 * day it was said (P53), that she has just paid on Stripe's page, in any tab, or that ops says a
 * bank payment on is clearing (`comingIn`), with that line, then what is still to pay of a part-paid
 * invoice, then the payments ops says were already made on it, each with its amount (`paymentsSaid`,
 * round six, slice S1), and ops's balance, in the size Paid's rows are drawn in, never Pay and never
 * paid (binding rule 1).
 */
function OnItsWayList({
  coming,
  homes,
  held,
  today,
}: {
  readonly coming: readonly Invoice[];
  readonly homes: PaymentsPlace['homes'];
  /** Whether her browser holds an invoice on its way, as the place draws it. */
  readonly held: (invoiceId: string) => boolean;
  /** The business's day the place was read on, which a payment of today is said as. */
  readonly today: string;
}) {
  return (
    <Rows
      heading={PAYMENTS.onItsWay}
      invoices={coming}
      homes={homes}
      said={(invoice) => [
        invoice.pay.clearing
          ? PAYMENTS.clearing
          : (onItsWayLine(invoice, held(invoice.id)) ?? PAYING.onItsWay),
        ...[partPaidOf(invoice)].filter((line): line is string => line !== null),
        ...paymentsSaid(invoice, today),
      ]}
      quiet={false}
      amount={(invoice) => invoice.balanceCents}
    />
  );
}

/** "Paid": each invoice paid, how and when it was paid as ops says, and its total. */
function PaidList({
  paid,
  homes,
  today,
}: {
  readonly paid: readonly Invoice[];
  readonly homes: PaymentsPlace['homes'];
  /** The business's day the place was read on, which a payment of today is said as. */
  readonly today: string;
}) {
  return (
    <Rows
      heading={PAYMENTS.paid}
      invoices={paid}
      homes={homes}
      said={(invoice) => howPaid(invoice, today)}
      quiet
      amount={(invoice) => invoice.totalCents}
    />
  );
}

/**
 * Each payment ops recorded on the invoice, in its order, in words, each led by its own amount
 * (`paymentWithAmount`, round six, slice S1).
 */
function paymentsSaid(invoice: Invoice, today: string): string[] {
  return (invoice.payments ?? []).flatMap((payment) => {
    const said = paymentWithAmount(payment, today);
    return said === null ? [] : [said];
  });
}

/**
 * How a paid invoice was paid, or nothing when ops does not say: each payment ops recorded on it
 * (`paymentsSaid`); else, while ops sends none, a charge to the saved method, with the receipt only
 * when ops says it emailed one; never both, so one payment is never said twice.
 */
function howPaid(invoice: Invoice, today: string): string[] {
  const payments = paymentsSaid(invoice, today);
  if (payments.length > 0) return payments;
  const { charged } = invoice;
  if (charged === undefined) return [];
  const day = charged.chargedOn === undefined ? null : dateInWords(charged.chargedOn);
  const said = chargedTo(charged.label, charged.methodType, day);
  return [charged.receiptEmailed ? `${said} ${PAYMENTS.receiptEmailed}` : said];
}

interface InvoiceRowProps {
  readonly invoice: Invoice;
  /** The name of its clean's home, when she sees several homes (`homeNameOf`). */
  readonly home: string | undefined;
  /** Its first line's words, where another row is named as this one is (`tellApart`). */
  readonly apart: string | undefined;
  /** What the row says under its day and its home, each line in turn, or none. */
  readonly said: readonly string[];
  readonly quiet: boolean;
  readonly amount: number | undefined;
}

/**
 * One row, one link to the invoice's own page: its day as the artboard writes a paid row's, its
 * home's name in body type, never muted, so two of one day read apart at a glance, its number,
 * what tells it apart, the row's line, and the amount, with the chevron that says it leads on.
 */
function InvoiceRow({ invoice, home, apart, said, quiet, amount }: InvoiceRowProps) {
  const days = daysOf(invoice);
  const day = days === null ? null : 'visit' in days ? days.visit : invoiceOf(days.issued);
  return (
    <Link
      to={invoicePath(invoice.id)}
      className="-mx-3 flex min-h-tap items-center gap-3 rounded-control px-3 py-2.5 hover:bg-surface-accent-soft"
    >
      <div className="min-w-0 flex-1">
        {day === null ? null : <p className="text-body font-semibold text-text-body">{day}</p>}
        {home === undefined ? null : <p className="text-body text-text-body">{home}</p>}
        {invoice.number === undefined ? null : (
          <p className={NUMBER}>{invoiceNumbered(invoice.number)}</p>
        )}
        {apart === undefined ? null : (
          <p className="text-small text-pretty wrap-break-word text-text-muted">{apart}</p>
        )}
        {said.map((line, index) => (
          <p
            key={index}
            className={`text-small text-pretty ${quiet ? 'text-text-muted' : 'mt-1 text-text-body'}`}
          >
            {line}
          </p>
        ))}
      </div>
      {amount === undefined ? null : (
        <p className="shrink-0 text-body font-semibold text-text-body">{dollars(amount)}</p>
      )}
      <Onward />
    </Link>
  );
}
